import { ChangeEvent, useEffect, useState } from "react";
import styled from "styled-components";
import { RoundedButton, Row, Timer, TextArea, Wrapper } from "../Components";
import { ScreenProps } from "../../types/ScreenTypes";
import { formatPhoneNumber } from "../../utils/phoneNumberUtils";
import { COMPLETE_CALL, PUBLISH_TO_CHANNEL } from "../../constants/buttonIds";
import { GetLocalStorage, urls } from "../api/api";
import axios from "axios";
import { useCallDurationTimer } from "../../hooks/useTimer";

const StyledRow = styled(Row)`
  justify-content: flex-start;
`;

function CallEndedScreen({
  cti,
  engagementId,
  handleCallCompleted,
  dialNumber,
  incomingNumber,
  direction,
  notes,
  setNotes,
  isCallRecorded,
  callDuration,
  callDurationString,
  callStatus,
}: ScreenProps) {
  const handleNotes = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setNotes(event.target.value);
  };
  const { startTimer, stopTimer, resetCallDuration } = useCallDurationTimer();

  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState<any>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);

  const userInformation = GetLocalStorage("userInformation");
  const UserInfo = userInformation ? JSON.parse(userInformation) : {};

  useEffect(() => {
    const timeoutId = setTimeout(async () => {
      try {
        const form = new FormData();
        form.append("authcode", UserInfo.authcode);

        const response = await axios.post(urls.calllogV2, form);
          await onSaveCall(response.data.result);
      } catch (error) {
        console.error("Error fetching call log:", error);
      } finally {
        setIsLoading(false);
      }
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [UserInfo.authcode]);

  const onSaveCall = async (recordData:any) => {
    try {
      setIsSaving(true)
      await new Promise((resolve) => setTimeout(resolve, 2000));
      const IncomingName = localStorage.getItem("incomingContactName");
      const callduration: number = Number(recordData[0]?.total_duration) * 1000;
      const engagementProperties = {
        hs_call_body: notes,
        hs_call_duration: callduration.toString(),
        hs_call_status: callStatus || "COMPLETED",
        hs_call_recording_url: recordData[0]?.file || "null",
        hs_call_disposition:
          recordData[0].callstatus !== "ANSWER"
            ? "73a0d17f-1163-4015-bdd5-ec830791da20"
            : "f240bbac-87c9-4f6e-bf70-924b57d47db7",
      };
      if (recordData[0].Flow_type === "IVR") {
        (
          engagementProperties as any
        ).hs_call_to_number = `+91${recordData[0]?.deskphone?.replace(
          /^0/,
          ""
        )}`;
        (
          engagementProperties as any
        ).hs_call_title = `Call with ${IncomingName}`;
        (engagementProperties as any).hs_call_direction = `INBOUND`;
        (engagementProperties as any).hs_call_from_number =
          `+91${recordData[0]?.caller_num?.replace(/^0/, "")}` || "Unknown ";
      } else {
        (engagementProperties as any).hs_call_to_number =
          `+91${recordData[0]?.caller_num?.replace(/^0/, "")}` || "Unknown ";
        (
          engagementProperties as any
        ).hs_call_title = `Call with ${IncomingName}`;
        (engagementProperties as any).hs_call_direction = `OUTBOUND`;
        (
          engagementProperties as any
        ).hs_call_from_number = `+91${recordData[0]?.deskphone?.replace(
          /^0/,
          ""
        )}`;
      }
      setSuccessMessage("Call saved successfully!");
      handleCallCompleted();
      localStorage.removeItem("incomingContactName");
      localStorage.removeItem("outgoing");
      localStorage.removeItem("isFromWindows");
      localStorage.removeItem("incomingCall");
      localStorage.removeItem("INCOMING_CALL");
      localStorage.removeItem("thirdParty");
    } catch (error: any) {
      setErrorMessage(
        error.message ||
          "An error occurred while saving the call. Please try again."
      );
    }
  };

  const onPublishChannel = async () => {
    try {
      if (!engagementId) {
        throw new Error("Engagement ID is missing. Cannot publish to channel.");
      }

      await cti.publishToChannel({ engagementId });

      setSuccessMessage("Published to channel successfully!");
    } catch (error: any) {
      setErrorMessage(
        error.message || "Failed to publish to the channel. Please try again."
      );
    }
  };

  return (
    <Wrapper>
      <div style={{ textAlign: "center" }}>
        <h2>
          Call with{" "}
          {formatPhoneNumber(
            direction === "OUTBOUND" ? dialNumber : incomingNumber
          )}{" "}
          ended
        </h2>
        <Timer>{callDurationString}</Timer>
      </div>
      {errorMessage ? (
        <p style={{ color: "red" }}>{errorMessage}</p>
      ) : (
        successMessage && <p style={{ color: "green" }}>{successMessage}</p>
      )}

      <StyledRow>Notes</StyledRow>
      <StyledRow>
        <TextArea
          id="callingnotes"
          rows={10}
          value={notes}
          onChange={handleNotes}
        />
      </StyledRow>
      <br />
      <Row>
        <RoundedButton
          use="primary"
          aria-label="save-call"
          data-test-id={COMPLETE_CALL}
          disabled={isSaving}
        >
          {isSaving ? "Saving..." : "Atuo Save Call"}
        </RoundedButton>
        <RoundedButton
          use="secondary"
          onClick={() => {
            setIsPublishing(true);
            onPublishChannel().finally(() => setIsPublishing(false));
          }}
          aria-label="publish-to-channel"
          data-test-id={PUBLISH_TO_CHANNEL}
          disabled={isPublishing}
        >
          {isPublishing ? "Publishing..." : "Publish to Channel"}
        </RoundedButton>
      </Row>
    </Wrapper>
  );
}

export default CallEndedScreen;
